import React, { useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { useCallRecordWithCtiContact } from 'flow/hooks/useCallRecordWithCtiContact';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import {
    selectCallRecordChatSessionDisplayName,
    selectCallRecordFromKey,
} from 'flow/selectors/call-selectors';
import { getContactFullName } from 'flow/utils/call-record-utils';
import {
    callRecordIsMissed,
    isCallRecordAMeeting,
} from 'flow/utils/call-records-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import CallRecordTypeIcon from 'flow/components/custom-components/CallRecordTypeIcon';
import CallRecordAudioPlayerV2 from 'flow/components/pbx/v2/misc/CallRecordAudioPlayerV2';
import CallRecordActions from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/CallRecordActions';
import {
    PageType,
    SegmentContextType,
} from 'flow/components/call-history-v2/types';
import DetailSection from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/DetailSection';
import MarkAsHandledButtonV2 from 'flow/components/pbx/v2/misc/MarkAsHandledButtonV2';
import BadgeVoiceMessageUnread from 'flow/components/pbx/v2/misc/BadgeVoiceMessageUnread';
import TitleRow from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/TitleRow';
import CallRecordAvatar from 'flow/components/call-history-v2/CallRecordAvatar';

interface Props {
    callRecordKey: CallRecordKey;
    queueKey?: QueueKey;
    className?: string;
    compact?: boolean;
    selected?: boolean;
    showMarkIsHandledIfMissed?: boolean;
    /**
     * Names the queue also when `queueKey` is given, for a list of calls from
     * many queues. A queue's own history leaves its name out.
     */
    showQueueName?: boolean;
    /**
     * The card sits on a surface of the colour it highlights with, as on the
     * Overview's widgets in dark mode. The highlight then goes one step
     * darker there, so hover and an open menu still show.
     */
    highlightOnCard?: boolean;
    showAvatar?: boolean;
    segmentContext: SegmentContextType;
    pageType: PageType;
    /**
     * Makes the contact name a link to this path, stretched over the whole
     * card. The card's own controls stay outside the link.
     */
    linkTo?: string;
    /** Called when the link from `linkTo` is clicked, before it navigates. */
    onLinkClick?: () => void;
    /**
     * Makes the contact name a button that opens the call, stretched over the
     * whole card as the link is, for a list whose calls have no page of their
     * own, such as the related calls. The card's own controls stay outside it.
     */
    onOpen?: () => void;
}

const CallRecordItemV2 = ({
    callRecordKey,
    queueKey,
    className,
    compact,
    selected,
    showMarkIsHandledIfMissed,
    showQueueName,
    highlightOnCard,
    showAvatar,
    segmentContext,
    pageType,
    linkTo,
    onLinkClick,
    onOpen,
}: Props) => {
    const { i18next } = useAppContext();
    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const rawCallRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );
    const callRecord = useCallRecordWithCtiContact(rawCallRecord);

    const videoDisplayName = useAppSelector((state) =>
        selectCallRecordChatSessionDisplayName(state, callRecordKey),
    );

    if (!callRecord) return null;

    const callIsMissedCall = callRecordIsMissed(callRecord);
    const isVideoCall = isCallRecordAMeeting(callRecord);
    const contactFullName = isVideoCall
        ? videoDisplayName || i18next.t('videoconference.meeting')
        : getContactFullName(callRecord);
    const hasVoiceMessage = !!callRecord.get('voiceMessage');
    const voiceMessageIsUnread =
        callRecord.getIn(['voiceMessage', 'voiceMessageState']) === 'unread';

    return (
        <div
            className={twMerge(
                'group relative flex min-h-12 flex-auto flex-col gap-1 rounded-sm p-2',
                compact ? 'whitespace-nowrap' : '',
                contextMenuIsActive
                    ? twMerge(
                          'bg-gray-100',
                          highlightOnCard && 'dark:bg-gray-200',
                      )
                    : twMerge(
                          'hover:bg-gray-100',
                          highlightOnCard && 'dark:hover:bg-gray-200',
                      ),
                className,
            )}
        >
            <BadgeVoiceMessageUnread visible={voiceMessageIsUnread} />
            <div className="flex">
                {showAvatar && (
                    <div className="me-2 flex items-start">
                        <CallRecordAvatar
                            callRecordKey={callRecordKey}
                            selected={selected}
                        />
                    </div>
                )}
                <div className="flex h-6 items-center">
                    <CallRecordTypeIcon
                        callRecordKey={callRecordKey}
                        className="size-5"
                        size="1.25rem"
                    />
                </div>

                <div className="flex w-full min-w-0 flex-col leading-6">
                    <TitleRow
                        callRecordKey={callRecordKey}
                        contactFullName={contactFullName}
                        callIsMissedCall={callIsMissedCall}
                        hideTimestamp={contextMenuIsActive}
                        pageType={pageType}
                        linkTo={linkTo}
                        onLinkClick={onLinkClick}
                        onOpen={onOpen}
                    />

                    <DetailSection
                        callRecordKey={callRecordKey}
                        contactFullName={contactFullName}
                        queueKeyContext={showQueueName ? undefined : queueKey}
                        compact={compact}
                        pageType={pageType}
                    />
                </div>
            </div>
            {/* After the title in the DOM, so Tab from the title link reaches
                the actions next; they are positioned absolutely, so the order
                changes nothing on screen. */}
            <CallRecordActions
                callRecordKey={callRecordKey}
                queueKey={queueKey}
                segmentContext={segmentContext}
                contextMenuIsActive={contextMenuIsActive}
                setContextMenuIsActive={setContextMenuIsActive}
            />
            {(hasVoiceMessage ||
                (callIsMissedCall && showMarkIsHandledIfMissed)) && (
                <div
                    className={twMerge(
                        // relative, and later in the DOM than the title link's
                        // stretched click area, so it sits above that area.
                        // No z-index: that would lift it above the sticky
                        // date header, which shares this stacking context.
                        // Clicks pass through it to the link, except where a
                        // child takes them back, so a click beside the
                        // controls opens the call.
                        'pointer-events-none relative flex flex-col gap-1',
                        showAvatar ? 'ms-18' : 'ms-7',
                    )}
                >
                    {hasVoiceMessage && (
                        // The player spans the card and keeps every click on
                        // itself, also one that misses its slider.
                        <div className="pointer-events-auto">
                            <CallRecordAudioPlayerV2
                                callRecordKey={callRecordKey}
                                selected={selected}
                            />
                        </div>
                    )}
                    {callIsMissedCall && showMarkIsHandledIfMissed && (
                        // The button is as wide as its text, and only it
                        // takes clicks.
                        <div className="[&_button]:pointer-events-auto">
                            <MarkAsHandledButtonV2
                                callRecordKey={callRecordKey}
                                segmentContext={segmentContext}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default React.memo(CallRecordItemV2);
